<template>
  <div>
    <div
      class="metrics-student-trajectories"
      data-testid="metrics-student-trajectories-card"
    >
      <div class="metrics-student-trajectories__header">
        <div class="metrics-student-trajectories__header-left">
          <span class="metrics-student-trajectories__title heading-s">{{
            $t("metrics.studentGrowthTrajectories")
          }}</span>
          <eg-icon
            icon="information"
            class="metrics-student-trajectories__info-icon"
            @click.native="
              $eventBus.$emit('onboarding:clicked:metricsStudentGrowthHint')
            "
          />
        </div>
        <div class="metrics-student-trajectories__search-wrap">
          <eg-icon
            icon="search"
            class="metrics-student-trajectories__search-icon"
          />
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="$t('essays.filters.searchStudent')"
            class="metrics-student-trajectories__search"
          />
        </div>
      </div>

      <p class="metrics-student-trajectories__subtitle paragraph-s">
        {{ $t("metrics.firstToLatest") }}
      </p>

      <div class="metrics-student-trajectories__filters">
        <eg-button
          v-for="option in trajectoryOptions"
          :key="option.value"
          type="secondary"
          :active="selectedTrajectory === option.value"
          class="metrics-student-trajectories__filter-card"
          @click="toggleTrajectory(option.value)"
        >
          <span
            class="metrics-student-trajectories__filter-dot"
            :style="{ background: option.color }"
          />
          <span class="metrics-student-trajectories__filter-label">{{
            option.label
          }}</span>
          <span class="metrics-student-trajectories__filter-stats">
            {{ option.percentage }}% ({{ option.count }})
          </span>
        </eg-button>
      </div>

      <div class="metrics-student-trajectories__body">
        <infinity-loading v-if="isLoading && !hasData" />

        <template v-else-if="filteredStudents.length > 0">
          <eg-pv-table
            :items="paginatedStudents"
            :fields="tableFields"
            @row-click="onRowClick"
          >
            <template #cell(student)="{ item }">
              <span class="metrics-student-trajectories__name">
                {{ item.firstName }} {{ item.lastName }}
              </span>
            </template>

            <template #cell(avgGrade)="{ item }">
              <span class="metrics-student-trajectories__avg-grade">
                {{ Math.round(item.avgGrade) }}%
              </span>
            </template>

            <template #cell(scores)="{ item }">
              <div
                class="metrics-student-trajectories__scores"
                :style="scoresWidth"
              >
                <template v-if="item.scores.length === 1">
                  <span
                    class="metrics-student-trajectories__score-bubble"
                    :class="scoreBubbleClass(item.scores[0])"
                    >{{ item.scores[0] }}%</span
                  >
                  <score-placeholder />
                  <score-placeholder />
                  <eg-icon
                    v-b-tooltip.hover="$t('metrics.insufficientDataTooltip')"
                    icon="information"
                    class="metrics-student-trajectories__info-icon metrics-student-trajectories__info-icon--row"
                  />
                </template>
                <template v-for="(score, index) in item.scores" v-else>
                  <span
                    :key="'score-' + index"
                    class="metrics-student-trajectories__score-bubble"
                    :class="scoreBubbleClass(score)"
                  >
                    {{ score }}%
                  </span>
                  <eg-icon
                    v-if="index < item.scores.length - 1"
                    :key="'arrow-' + index"
                    icon="arrow-right"
                    class="metrics-student-trajectories__score-arrow"
                  />
                </template>
              </div>
            </template>

            <template #cell(growth)="{ item }">
              <span
                class="metrics-student-trajectories__growth-badge"
                :class="growthBadgeClass(item.growth)"
              >
                <eg-icon
                  :icon="item.growth >= 0 ? 'arrow-up' : 'arrow-down'"
                  class="metrics-student-trajectories__growth-icon"
                />
                {{ item.growth > 0 ? "+" : "" }}{{ item.growth }}%
                {{ $t("common.points") }}
              </span>
            </template>
          </eg-pv-table>
        </template>

        <metrics-empty-state v-else :description="emptyStateDescription" />
      </div>

      <metrics-pagination
        v-if="hasData"
        :value="currentPage"
        :total-rows="filteredStudents.length"
        :per-page="pageSize"
        @change="currentPage = $event"
      >
        <template #label>
          {{
            $t("metrics.showingRange", {
              start: paginationStart,
              end: paginationEnd,
              total: filteredStudents.length,
            })
          }}
        </template>
      </metrics-pagination>

      <template v-if="isExportingPdf && suggestions">
        <div
          v-for="option in trajectoryOptions"
          v-show="suggestions[option.value] && option.count > 0"
          :key="option.value"
          class="metrics-student-trajectories__hint"
        >
          <div class="metrics-student-trajectories__hint-label">
            <eg-icon icon="bulb-light" />
            <span
              >{{ $t("metrics.aiRecommendation") }} — {{ option.label }}</span
            >
          </div>
          <p class="metrics-student-trajectories__hint-text paragraph-s">
            {{ suggestions[option.value] }}
          </p>
        </div>
      </template>

      <div
        v-else-if="
          suggestions &&
          selectedTrajectory &&
          suggestions[selectedTrajectory] &&
          filteredStudents.length > 0
        "
        class="metrics-student-trajectories__hint"
      >
        <div class="metrics-student-trajectories__hint-label">
          <eg-icon icon="bulb-light" />
          <span>{{ $t("metrics.aiRecommendation") }}</span>
        </div>
        <p class="metrics-student-trajectories__hint-text paragraph-s">
          {{ suggestions[selectedTrajectory] }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import MetricsEmptyState from "../shared/metrics_empty_state.vue";
import InfinityLoading from "@/components/rubrics/newRubric/components/infinity-loading.vue";
import MetricsPagination from "../shared/metrics_pagination.vue";
import ScorePlaceholder from "./metrics_score_placeholder.vue";
import EgPvTable from "@/components/global/eg/eg_pv_table/eg_pv_table.vue";
import metricsViewMixin from "@/mixins/metricsViewMixin";

const PAGE_SIZE = 6;

const TRAJECTORY_CONFIG = [
  {
    value: "improving",
    labelKey: "metrics.improvingStudents",
    color: "#60A5FA",
  },
  {
    value: "declining",
    labelKey: "metrics.decliningStudents",
    color: "#F87171",
  },
  {
    value: "inconsistent",
    labelKey: "metrics.inconsistentStudents",
    color: "#FBBF24",
  },
  { value: "stable", labelKey: "metrics.stableStudents", color: "#94A3B8" },
];

export default {
  name: "MetricsStudentTrajectories",

  components: {
    MetricsEmptyState,
    InfinityLoading,
    MetricsPagination,
    ScorePlaceholder,
    EgPvTable,
  },

  mixins: [metricsViewMixin],

  props: {
    trajectories: {
      type: Object,
      default: () => ({ summary: {}, data: [] }),
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    suggestions: {
      type: Object,
      default: null,
    },
    isExportingPdf: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      searchQuery: "",
      selectedTrajectory: null,
      currentPage: 1,
      pageSize: PAGE_SIZE,
    };
  },

  computed: {
    students() {
      return this.trajectories?.data || [];
    },

    summary() {
      return this.trajectories?.summary || {};
    },

    trajectoryOptions() {
      return TRAJECTORY_CONFIG.map((config) => ({
        ...config,
        label: this.$t(config.labelKey),
        count: this.summary[config.value]?.count || 0,
        percentage: this.summary[config.value]?.percentage || 0,
      }));
    },

    hasData() {
      return this.students && this.students.length > 0;
    },

    hasOnlyOneAssignment() {
      return (
        this.students.length > 0 &&
        this.students.some((s) => s.scores.length === 1)
      );
    },

    scoresWidth() {
      if (!this.students.length) return {};
      const max = Math.max(...this.students.map((s) => s.scores.length));
      const displayCount = max === 1 ? 3 : max;
      return { width: `${58 * displayCount}px` };
    },

    filteredStudents() {
      let result = [...this.students];

      if (this.searchQuery.trim()) {
        const q = this.searchQuery.trim().toLowerCase();
        result = result.filter((s) =>
          `${s.firstName} ${s.lastName}`.toLowerCase().includes(q)
        );
      }

      if (this.selectedTrajectory && !this.isExportingPdf) {
        result = result.filter((s) => s.trajectory === this.selectedTrajectory);
      }

      return result;
    },

    paginationStart() {
      return (this.currentPage - 1) * PAGE_SIZE + 1;
    },

    paginationEnd() {
      return Math.min(
        this.currentPage * PAGE_SIZE,
        this.filteredStudents.length
      );
    },

    paginatedStudents() {
      if (this.isExportingPdf) return this.filteredStudents;
      const start = (this.currentPage - 1) * PAGE_SIZE;
      return this.filteredStudents.slice(start, start + PAGE_SIZE);
    },

    emptyStateDescription() {
      if (this.hasData && this.selectedTrajectory) {
        return this.$t("metrics.noStudentsInTrajectoryDescription");
      }
      return null;
    },

    tableFields() {
      return [
        {
          key: "student",
          label: this.$t("metrics.student"),
          style: "width: 250px",
        },
        {
          key: "avgGrade",
          label: this.$t("metrics.averageGrade"),
          style: "width: 140px; text-align: center",
        },
        {
          key: "scores",
          label: this.$t("metrics.scorePerAssignmentHeader"),
          style: "text-align: center",
        },
        {
          key: "growth",
          label: this.$t("metrics.growth"),
          style: "width: 140px; text-align: center",
        },
      ];
    },
  },

  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
    selectedTrajectory() {
      this.currentPage = 1;
    },
  },

  methods: {
    onRowClick(item) {
      if (!item.studentId) {
        return;
      }
      const classId =
        this.$route.params.classId ||
        this.$store.getters["customReport/classIds"][0];
      if (!classId) {
        return;
      }
      this.goToStudent(item.studentId, classId);
    },

    toggleTrajectory(value) {
      this.selectedTrajectory =
        this.selectedTrajectory === value ? null : value;
    },

    scoreBubbleClass(score) {
      if (score >= 85)
        return "metrics-student-trajectories__score-bubble--high";
      if (score >= 66)
        return "metrics-student-trajectories__score-bubble--medium";
      return "metrics-student-trajectories__score-bubble--low";
    },

    growthBadgeClass(growth) {
      if (growth > 0)
        return "metrics-student-trajectories__growth-badge--positive";
      if (growth < 0)
        return "metrics-student-trajectories__growth-badge--negative";
      return "metrics-student-trajectories__growth-badge--neutral";
    },
  },
};
</script>

<style lang="scss" scoped>
.metrics-student-trajectories {
  display: flex;
  flex-direction: column;
  padding: 24px 24px 0;
  margin: 0 24px;
  background: var(--color-surface-neutral-background, #fff);
  border: 1px solid var(--color-stroke-neutral-default, #e5ebf0);
  border-radius: 12px;
  overflow: hidden;

  ::v-deep .metrics-pagination {
    margin: 0 -24px;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 4px;
    gap: 16px;
  }

  &__header-left {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
  }

  &__title {
    font-size: var(--typography-font-size-fs-600);
    font-weight: var(--typography-font-weight-fw-700, 700);
    color: var(--color-text-neutral-on-white, #1a2330);
  }

  &__info-icon {
    color: var(--color-icon-neutral-secondary, #8a97a4);
    font-size: 14px;
    cursor: help;

    &--row {
      margin-left: 6px;
      flex-shrink: 0;
    }
  }

  &__search-wrap {
    position: relative;
    width: 220px;
    flex-shrink: 0;
  }

  &__search-icon {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--color-icon-neutral-secondary, #8a97a4);
    font-size: 14px;
    pointer-events: none;
  }

  &__search {
    width: 100%;
    padding: 6px 10px 6px 32px;
    border: 1px solid var(--color-stroke-neutral-default, #e5ebf0);
    border-radius: var(--radius-rd-300, 8px);
    background: var(--color-surface-neutral-container-1, #f7f9fa);
    color: var(--color-text-neutral-on-white, #1a2330);
    font-size: var(--typography-font-size-fs-300, 14px);
    font-family: inherit;
    outline: none;

    &::placeholder {
      color: var(--color-text-neutral-fade, #8a97a4);
    }

    &:focus {
      border-color: var(--color-stroke-brand-default, #3b82f6);
      background: #fff;
    }
  }

  &__subtitle {
    color: var(--color-text-neutral-fade);
    margin: 0 0 16px;
  }

  &__filters {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 16px;
  }

  &__filter-card {
    flex: 1 1 auto;
    padding: 12px 14px;
    justify-content: flex-start;

    @media (max-width: 1399px) {
      flex: none;
    }

    @media (max-width: 1129px) {
      flex: 1;
    }
    &.eg-new-button--active.eg-new-button--color-purple {
      background: var(--color-surface-primary-light-1, #f6f2ff);
    }
    &:not(.eg-new-button--active).eg-new-button--color-purple {
      border-color: var(--color-stroke-neutral-default);
    }

    ::v-deep .button-text {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
    }
  }

  &__filter-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  &__filter-label {
    font-size: var(--typography-font-size-fs-300, 14px);
    font-weight: var(--typography-font-weight-fw-600, 600);
    color: var(--color-text-neutral-on-white, #1a2330);
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-align: left;
  }

  &__filter-stats {
    font-size: var(--typography-font-size-fs-300, 14px);
    font-weight: var(--typography-font-weight-fw-500, 500);
    color: var(--color-text-neutral-secondary, #566370);
    white-space: nowrap;
  }

  &__body {
    margin-top: 16px;
    margin-bottom: 24px;

    ::v-deep .p-datatable-tbody > tr {
      cursor: pointer;
    }
  }

  &__name {
    font-size: var(--typography-font-size-fs-300);
    font-weight: var(--typography-font-weight-fw-500, 500);
    color: var(--color-text-neutral-on-white, #1a2330);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__avg-grade {
    font-size: var(--typography-font-size-fs-300);
    font-weight: var(--typography-font-weight-fw-700, 700);
    color: var(--color-text-neutral-on-white, #1a2330);
  }

  &__scores {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-wrap: nowrap;
    justify-content: start;
    min-width: 220px;
    margin: 0 auto;
  }

  &__score-bubble {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: var(--typography-font-size-fs-200);
    font-weight: var(--typography-font-weight-fw-600, 600);
    min-width: 52px;

    &--placeholder {
      background: var(--color-surface-neutral-container-3, #e5ebf0);
      color: var(--color-text-neutral-secondary, #566370);
    }
    &--high {
      background: var(--colors-green-100, #d9f2e0);
      color: var(--color-text-semantic-onSuccess, #308c4a);
    }

    &--medium {
      background: rgba(96, 165, 250, 0.12);
      color: #60a5fa;
    }

    &--low {
      background: rgba(248, 113, 113, 0.12);
      color: #f87171;
    }
  }

  &__score-arrow {
    font-size: 10px;
    color: var(--color-icon-neutral-secondary, #8a97a4);
  }

  &__growth-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 10px;
    border-radius: 100px;
    font-size: var(--typography-font-size-fs-200, 12px);
    font-weight: var(--typography-font-weight-fw-700, 700);
    flex-wrap: nowrap;
    white-space: nowrap;
    &--positive {
      background: var(--colors-green-100, #d9f2e0);
      color: var(--color-text-semantic-onSuccess, #308c4a);
    }

    &--negative {
      background: rgba(248, 113, 113, 0.12);
      color: #f87171;
    }

    &--neutral {
      background: var(--color-surface-neutral-container-1, #f7f9fa);
      color: var(--color-text-neutral-secondary, #566370);
    }
  }

  &__growth-icon {
    font-size: 10px;
  }

  &__hint {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 0.75rem;
    margin: 12px 0;
    border-radius: 8px;
    background: var(--color-surface-semantic-informative-light);
  }

  &__hint-label {
    display: flex;
    align-items: center;
    gap: 5px;
    color: var(--color-text-semantic-informative, #17a2b8);
    font-size: var(--typography-font-size-fs-200);
    font-weight: var(--typography-font-weight-fw-500);
    line-height: 135%;
    letter-spacing: 0;
  }

  &__hint-text {
    margin: 0;
    color: var(--color-text-neutral-secondary, #566370);
    line-height: 1.5;
  }
  ::v-deep .p-datatable-thead th:nth-child(3) .p-column-header-content {
    justify-content: center;
  }
}
</style>
